import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Globe, Plus, Loader2, Check, Headphones, Clock, Calendar, ListMusic, Mic } from 'lucide-react';
import { getLanguageName } from '../services/languageConstants';
import { translationService } from '../services/translationService';
import { RatingBadge } from '../components/RatingStars';

// Janela de informações do audiobook em /audiobooks. Mesmo visual e animação da janela de detalhes
// de livro de /libritui-library (pages/BookDetailsModal.tsx): palco pastel com a capa à esquerda,
// dados e descrição (com "Traduzir") à direita, botões em pílula embaixo.

export interface AudiobookCardItem {
  id: string;
  slug?: string;
  title: string;
  author: string;
  language: string;
  folder?: string;
  cover_url?: string;
  duration_seconds?: number;
  year?: string | null;
  tracks?: number;
  genres?: string[];
  is_favorited?: boolean;
  is_saved?: boolean;
  rating_avg?: number | null;
  rating_count?: number;
}

interface Details {
  description?: string;
  tags?: string[];
  genres?: string[];
  readers?: string[];
  year?: string | null;
  tracks?: number;
  duration_seconds?: number;
  rating_avg?: number | null;
  rating_count?: number;
}

const LANG_FLAGS: Record<string, string> = { fr: '🇫🇷', it: '🇮🇹', es: '🇪🇸', pt: '🇧🇷', de: '🇩🇪', en: '🇺🇸', nl: '🇳🇱' };

const formatDuration = (secs?: number) => {
  if (!secs || secs <= 0) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

interface Props {
  item: AudiobookCardItem;
  onClose: () => void;
  onListen: (item: AudiobookCardItem) => void;
  isOpening: boolean;
  isSaved: boolean;
  isSaving: boolean;
  onSave: (item: AudiobookCardItem) => void;
  renderCover: (item: AudiobookCardItem) => React.ReactNode;
}

const AudiobookDetailsModal: React.FC<Props> = ({ item, onClose, onListen, isOpening, isSaved, isSaving, onSave, renderCover }) => {
  const { t } = useTranslation();
  const [details, setDetails] = useState<Details | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [translatedDescription, setTranslatedDescription] = useState<string | null>(null);
  const [isTranslatingDesc, setIsTranslatingDesc] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoadingDetails(true);
    fetch(`/api/sync-audiobooks/${encodeURIComponent(item.id)}/details`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setDetails(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingDetails(false); });
    return () => { alive = false; };
  }, [item.id]);

  useEffect(() => {
    const originalStyle = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalStyle;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const description = details?.description || '';

  // Mesmo comportamento do "Traduzir" da janela de livros: protege trechos entre aspas e traduz para o idioma da interface
  const handleTranslateDescription = async () => {
    if (translatedDescription) { setTranslatedDescription(null); return; }
    if (!description) return;
    setIsTranslatingDesc(true);
    try {
      const nativeLang = (localStorage.getItem('i18nextLng') || 'pt').split('-')[0].toLowerCase();
      const placeholders: string[] = [];
      const protectedText = description.replace(/(["'“‘«])([\s\S]*?)(["'”’»])/g, (match) => {
        placeholders.push(match);
        return `__BOOK_QUOTE_TOKEN_${placeholders.length - 1}__`;
      });
      const res = await translationService.unifiedTranslate(protectedText, nativeLang, undefined, item.language || 'en');
      if (res && res.translation) {
        let finalTranslation = res.translation;
        placeholders.forEach((orig, idx) => {
          finalTranslation = finalTranslation.replace(new RegExp(`__BOOK_QUOTE_TOKEN_${idx}__`, 'gi'), orig);
        });
        setTranslatedDescription(finalTranslation);
      }
    } catch (e) {
      console.warn('Erro ao traduzir descrição do audiobook:', e);
    } finally {
      setIsTranslatingDesc(false);
    }
  };

  const langCode = (item.language || 'en').toLowerCase();
  const duration = formatDuration(details?.duration_seconds || item.duration_seconds);
  const year = details?.year || item.year;
  const tracks = details?.tracks || item.tracks;
  const readers = (details?.readers || []).filter(Boolean);
  const tags = Array.from(new Set([...(details?.genres || item.genres || []), ...(details?.tags || [])])).slice(0, 8);
  const ratingAvg = details?.rating_avg ?? item.rating_avg;
  const ratingCount = details?.rating_count ?? item.rating_count;

  return createPortal(
    <div
      className="fixed inset-0 w-screen h-screen flex items-center justify-center bg-black/30 backdrop-blur-[20px] animate-in fade-in duration-300"
      style={{ zIndex: 200, padding: '1rem' }}
      onClick={onClose}
    >
      <div
        className="bg-surface w-full max-w-4xl rounded-[32px] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-500 relative flex flex-col md:flex-row border border-black/[0.06] dark:border-white/[0.06] shadow-[0_40px_100px_-40px_rgba(0,0,0,0.6)]"
        style={{ maxHeight: '88vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-primary/[0.06] hover:bg-primary hover:text-slate-900 text-ink rounded-full z-[70] flex items-center justify-center transition-colors"
          aria-label="close"
        >
          <X size={18} strokeWidth={1.75} />
        </button>

        {/* Capa: o audiobook "flutua" sobre um palco em tom pastel */}
        <div className="w-full md:w-[42%] flex-shrink-0 bg-tint-stone relative overflow-hidden flex items-center justify-center py-8 md:py-12">
          <span aria-hidden className="absolute -top-20 -left-16 w-72 h-72 rounded-full bg-tint-lavender/70 blur-3xl" />
          <span aria-hidden className="absolute -bottom-24 -right-10 w-72 h-72 rounded-full bg-tint-peach/60 blur-3xl" />
          <div className="relative w-40 md:w-60 aspect-square rounded-[18px] overflow-hidden shadow-[0_30px_60px_-24px_rgba(0,0,0,0.55)] rotate-[-2deg] transition-transform duration-700 hover:rotate-0 hover:scale-[1.03]">
            {renderCover(item)}
          </div>
        </div>

        <div className="flex-1 p-6 md:p-10 flex flex-col gap-6 overflow-y-auto custom-scrollbar">
          <div className="pr-10">
            <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted flex items-center gap-2">
              <Headphones size={13} strokeWidth={1.75} /> {t('audiobooks.kind_label', 'audiobook com texto')}
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-ink leading-[1.08] tracking-[-0.035em] mt-2">
              {item.title}<span className="text-primary">.</span>
            </h2>
            {item.author && <p className="text-muted text-[15px] mt-2">{item.author}</p>}
            {ratingAvg ? <RatingBadge avg={ratingAvg} count={ratingCount} className="mt-3 text-[13px]" /> : null}
          </div>

          {/* Idioma, duração, ano, faixas e tags */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="h-9 pl-2 pr-3.5 rounded-full bg-tint-sky text-ink text-sm flex items-center gap-2">
              <span className="text-base leading-none">{LANG_FLAGS[langCode] || '🌐'}</span>
              {t('languages.' + langCode, getLanguageName(langCode))}
            </span>
            {duration && (
              <span className="h-9 px-3.5 rounded-full bg-tint-sage text-ink text-sm flex items-center gap-2">
                <Clock size={13} strokeWidth={1.75} /> {duration}
              </span>
            )}
            {year && (
              <span className="h-9 px-3.5 rounded-full bg-tint-butter text-ink text-sm flex items-center gap-2">
                <Calendar size={13} strokeWidth={1.75} /> {t('audiobooks.published_in', { year, defaultValue: 'Publicado em {{year}}' })}
              </span>
            )}
            {!!tracks && (
              <span className="h-9 px-3.5 rounded-full bg-tint-lavender text-ink text-sm flex items-center gap-2">
                <ListMusic size={13} strokeWidth={1.75} /> {t('audiobooks.tracks_count', { count: tracks, defaultValue: '{{count}} faixas' })}
              </span>
            )}
            {tags.map((tag, idx) => (
              <span key={idx} className="h-7 px-3 rounded-full bg-ink/[0.05] text-[11px] text-ink/70 flex items-center">{tag}</span>
            ))}
          </div>

          {readers.length > 0 && (
            <p className="text-[13px] text-muted flex items-center gap-2 -mt-2">
              <Mic size={13} strokeWidth={1.75} /> {t('audiobooks.read_by', { names: readers.slice(0, 3).join(', '), defaultValue: 'Narração: {{names}}' })}
            </p>
          )}

          {/* Descrição */}
          <div className="bg-ink/[0.03] dark:bg-white/[0.03] rounded-[22px] p-5 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted">{t('audiobooks.about', 'sobre o audiobook')}</span>
              {description && (
                <button
                  onClick={handleTranslateDescription}
                  disabled={isTranslatingDesc}
                  className={`h-8 pl-2.5 pr-3 rounded-full text-xs flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-60 ${translatedDescription ? 'bg-ink text-surface' : 'bg-ink/[0.06] hover:bg-ink/[0.1] text-ink'}`}
                >
                  {isTranslatingDesc ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} strokeWidth={1.75} />}
                  <span>{isTranslatingDesc ? t('audiobooks.translating', 'Traduzindo...') : translatedDescription ? t('audiobooks.show_original', 'Ver original') : t('audiobooks.translate', 'Traduzir')}</span>
                </button>
              )}
            </div>
            <div className="max-h-40 overflow-y-auto custom-scrollbar pr-2">
              {loadingDetails ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-3 rounded-full bg-ink/[0.06] w-full" />
                  <div className="h-3 rounded-full bg-ink/[0.06] w-11/12" />
                  <div className="h-3 rounded-full bg-ink/[0.06] w-3/4" />
                </div>
              ) : description ? (
                <p className="text-[14px] text-ink/75 leading-relaxed">{translatedDescription || description}</p>
              ) : (
                <p className="text-sm text-muted italic leading-relaxed">{t('audiobooks.no_description', 'Nenhuma descrição disponível para este audiobook.')}</p>
              )}
            </div>
          </div>

          <div className="mt-auto pt-1 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => onListen(item)}
              disabled={isOpening}
              className="group flex-1 h-14 pl-6 pr-2 bg-primary text-slate-900 rounded-full text-sm font-medium flex items-center justify-between gap-3 hover:scale-[1.01] active:scale-95 transition-transform disabled:opacity-50"
            >
              <span>{t('audiobooks.listen_and_read', 'Ouvir e ler')}</span>
              <span className="w-10 h-10 rounded-full bg-surface/15 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5">
                {isOpening ? <Loader2 size={17} className="animate-spin" /> : <Headphones size={17} strokeWidth={1.75} />}
              </span>
            </button>
            <button
              onClick={() => { if (!isSaved) onSave(item); }}
              disabled={isSaving}
              className={`group flex-1 h-14 pl-6 pr-2 rounded-full text-sm font-medium flex items-center justify-between gap-3 hover:scale-[1.01] active:scale-95 transition-transform disabled:opacity-50 border border-black/[0.06] dark:border-white/[0.06] ${isSaved ? 'bg-tint-sage text-ink' : 'bg-surface text-ink'}`}
            >
              <span>{isSaved ? t('audiobooks.on_shelf', 'Na estante') : t('library.saveToShelf', 'Salvar na Estante')}</span>
              <span className="w-10 h-10 rounded-full bg-ink/[0.06] flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5">
                {isSaving ? <Loader2 size={17} className="animate-spin" /> : isSaved ? <Check size={17} strokeWidth={1.75} /> : <Plus size={17} strokeWidth={1.75} />}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AudiobookDetailsModal;
